import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  handleAdminHighRiskSupplementsList,
  handleAdminImportMedications,
  handleAdminImportSynonymsList,
} from '@store/features/adminImportMedications/actions';
import {
  adminImportHighRiskSupplementsListErrorsSelector,
  adminImportHighRiskSupplementsListLoadingSelector,
  adminImportMedicationsErrorsSelector,
  adminImportMedicationsLoadingSelector,
  adminImportSynonymsListErrorsSelector,
  adminImportSynonymsListLoadingSelector,
} from '@store/features/adminImportMedications/selectors';
import {
  removeAdminImportHighRiskSupplementsList,
  removeAdminImportMedications,
  removeAdminImportSynonymsList,
} from '@store/features/adminImportMedications/slice';
import useDispatch from '@store/hooks/useDispatch';
import FileUploader from '@views/AdminPage/ImportMedications/FileUploader/FileUploader';
import { EventType } from '@utils/eventBus';

import './style.scss';

const ImportMedications: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isLoadingMedications = useSelector(adminImportMedicationsLoadingSelector);
  const errorsMedications = useSelector(adminImportMedicationsErrorsSelector);
  const hasErrorsMedications = errorsMedications.length > 0;

  const isLoadingSynonymsList = useSelector(adminImportSynonymsListLoadingSelector);
  const errorsSynonymsList = useSelector(adminImportSynonymsListErrorsSelector);
  const hasErrorsSynonymsList = errorsSynonymsList.length > 0;

  const isLoadingHighRiskSupplementsList = useSelector(
    adminImportHighRiskSupplementsListLoadingSelector,
  );
  const errorsHighRiskSupplementsList = useSelector(
    adminImportHighRiskSupplementsListErrorsSelector,
  );
  const hasErrorsHighRiskSupplementsList = errorsHighRiskSupplementsList.length > 0;

  useEffect(() => {
    dispatch(removeAdminImportMedications());
    dispatch(removeAdminImportSynonymsList());
    dispatch(removeAdminImportHighRiskSupplementsList());
  }, [dispatch]);

  return (
    <div className="import-medications">
      <div className="import-medications__title">{t('admin.importMedicationsHeader')}</div>

      <FileUploader
        className="import-medications__file-uploader"
        importHandler={handleAdminImportMedications}
        errors={errorsMedications}
        eventType={EventType.ADMIN_IMPORT_MEDICATIONS_SUCCESS}
        hasErrors={hasErrorsMedications}
        isLoading={isLoadingMedications}
        successMessage={t('admin.importMedicationsSuccess')}
      />

      <div className="import-medications__title">{t('admin.importSynonymsListHeader')}</div>

      <FileUploader
        className="import-medications__file-uploader"
        importHandler={handleAdminImportSynonymsList}
        errors={errorsSynonymsList}
        eventType={EventType.ADMIN_IMPORT_SYNONYMS_LIST_SUCCESS}
        hasErrors={hasErrorsSynonymsList}
        isLoading={isLoadingSynonymsList}
        successMessage={t('admin.importSynonymsListSuccess')}
      />

      <div className="import-medications__title">
        {t('admin.importHighRiskSupplementsListHeader')}
      </div>

      <FileUploader
        className="import-medications__file-uploader"
        importHandler={handleAdminHighRiskSupplementsList}
        errors={errorsHighRiskSupplementsList}
        eventType={EventType.ADMIN_IMPORT_HIGH_RISK_SUPPLEMENTS_LIST_SUCCESS}
        hasErrors={hasErrorsHighRiskSupplementsList}
        isLoading={isLoadingHighRiskSupplementsList}
        successMessage={t('admin.importHighRiskSupplementsSuccess')}
      />
    </div>
  );
};

export default ImportMedications;
